import { useState, useCallback, useEffect } from 'react'
import { UserPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import { getNextOrder } from '../../../../../baseComponents/TwinTable/functions'
import { InputTableURLsTable } from '../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { DefaultOrder, TableConfiguration } from '../../../../../baseComponents/TwinTable/types'
import { twinFetchPostJSON } from '../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { OrderData } from '../types'
import { addMultiHeaderLine, addNLineMultiToOrderData } from '../useMultiOrderScreenLogic/functions'
import { buildOrderFieldsOrderLayout, cleanTableDataForOriginalTable, groupTableDataByVariantParent, groupTableDataByAddonParent, parseOrderTags, addErrorsOfTableDataInHeader, parseScalesOfOrderData } from '../useOrderScreenLogic/functions'
import useInputTableFilterData from '../../../../../utils/hooks/orders/useInputTableFilterData'
import { fillRefundHeaderAfter, handleRefundData } from './functions'
import { recalculateAllQuoteOrderRows } from '@teinor/erp/prices/quote/calculate'
import { WorkingCenterPayload } from '../../../../../utils/reducers/company/workingCenters'

interface MultiOrderScreenLogicProps {
    inputTableURLs: InputTableURLsTable
    userPermissions: UserPermissions
    defaultOrder?: DefaultOrder
    type: 'sellInvoiceHeader' | 'sellDeliveryNoteHeader'
    workingCenters: WorkingCenterPayload
}

const useMultiOrderRefundLogic = ({ inputTableURLs, defaultOrder, type, workingCenters }: MultiOrderScreenLogicProps) => {
    const [completeData, setCompleteData] = useState<OrderData | null>(null)
    const [tableConfiguration, setTableConfiguration] = useState<TableConfiguration>({
        generalSearchbar: '',
        order: {},
        offset: 0,
        singleFieldsSearch: {},
        petitionNumber: 0
    })
    const { parseTableData } = useInputTableFilterData({ setCompleteData })
    const changeOrder = useCallback((fieldName: string) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            const newOrder = getNextOrder(fieldName, copyOld.order, defaultOrder?.order || 'desc')
            return {
                ...copyOld,
                offset: 0, 
                order: {...newOrder}
            }
        })
    }, [setTableConfiguration, defaultOrder?.order])

    const changeSingleField = useCallback((fieldName: string, value: string | string[]) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset: 0,
                singleFieldsSearch: {
                    ...copyOld.singleFieldsSearch,
                    [fieldName]: value
                }
            }
        })
    }, [setTableConfiguration])

    const myTableConfigurationStr = JSON.stringify(tableConfiguration)
    const getDataStr = JSON.stringify(inputTableURLs.getData)
    const typeStr = JSON.stringify(type)
    const getCompleteData = useCallback(async () => {
        const myTableConfigurationParsed = JSON.parse(myTableConfigurationStr)
        const getDataParsed = JSON.parse(getDataStr)
        const typeParsed = JSON.parse(typeStr)
        const order = buildOrderFieldsOrderLayout(myTableConfigurationParsed.order)
        let orderData: OrderData = {
            header: {}, tableData: [], originalTableData: [],
            dictPosTableData: {},
            filters: {
                generalSearchbar: '',
                order: {},
                singleFieldsSearch: {}
            }
        }
        let result = await twinFetchPostJSON(getDataParsed[0], { ...getDataParsed[1], includeRelations: true, order })
        let header: TwinDictionary = {}
        let full_assigned_number_parsed: string[] = []
        let nLine = 1
        for (let myOrder of result) {
            if (myOrder?.header?.full_assigned_number){
                full_assigned_number_parsed.push(myOrder.header.full_assigned_number)
            }
            header = { ...myOrder.header, ...fillRefundHeaderAfter(myOrder.header) }
            orderData.header = { ...header, ...getDataParsed[1] }
            if (myOrder['tableData']?.length) {
                myOrder.originalTableData = cleanTableDataForOriginalTable(myOrder.tableData)
                orderData.originalTableData = [...orderData.originalTableData, ...myOrder.originalTableData]
                myOrder.tableData = addMultiHeaderLine(myOrder.tableData, myOrder, typeParsed)
                myOrder.tableData = groupTableDataByVariantParent(myOrder.tableData)
                myOrder.tableData = groupTableDataByAddonParent(myOrder.tableData)
                myOrder.tableData = handleRefundData(myOrder.tableData)
                myOrder.header = parseOrderTags(myOrder.header)
                myOrder = addErrorsOfTableDataInHeader(myOrder)
                const { myOrderData, position } = addNLineMultiToOrderData(myOrder, nLine)
                myOrder = myOrderData
                nLine = position
                orderData.tableData = [...orderData.tableData, ...myOrder.tableData]
            }
        }
        orderData = parseScalesOfOrderData(orderData)
        orderData.header.full_assigned_number_parsed = full_assigned_number_parsed
        orderData.header.WarehouseId = workingCenters?.[orderData.header.WorkingCenterId]?.warehouseMain
        orderData.header.warehouse_conflicts = workingCenters?.[orderData.header.WorkingCenterId]?.Warehouses?.length !== 1 ? workingCenters?.[orderData.header.WorkingCenterId]?.Warehouses : null
        recalculateAllQuoteOrderRows(orderData)
        let processedTableLength = 0
        orderData.dictPosTableData = {}
        for (const key in orderData.tableData) {
            const element = orderData.tableData[key]
            if(element.processed){
                processedTableLength++
            }
            const myParsedKey = parseInt(key)
            element.absoluteIndex = myParsedKey
            orderData.dictPosTableData[element.id] = myParsedKey
        }
        orderData.processedTableLength = processedTableLength
        return parseTableData((old) => old, orderData)
    }, [getDataStr, myTableConfigurationStr, parseTableData, typeStr, workingCenters])
    
    useEffect(() => {
        getCompleteData()
    }, [getCompleteData])

    return { completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, headerChanges: { changeOrder, changeSingleField } }
}

export default useMultiOrderRefundLogic
